import { z } from 'zod';
import { createModel, partySchema, QueryMany, QueryOne } from '../../common';
import { objectIdSchema } from '../../validation';

/**
 * Kinds of `AgentActivity` rows. Each kind has bilingual title
 * templates owned by the tool / graph that emits it (see
 * `docs/ai/tools.md` and `docs/ai/observability.md`).
 *
 * - `tool_started` / `tool_completed` / `tool_failed` — fire-and-forget
 *   per tool invocation. Render as inline activity chips between
 *   bubbles.
 * - Domain-event kinds (`order_created`, `driver_assigned`, …) — also
 *   rendered on the order page timeline alongside system events.
 * - `subscription_blocked` — emits when the paywall
 *   (`SubscriptionEntitlementService`) holds an action. The row is what
 *   answers "why didn't it issue?" for support, since the user only sees
 *   the reply asking them to subscribe.
 * - `escalated_to_human` — emits when `escalate_to_human` flips the
 *   responder mode.
 * - `task_started` / `task_completed` / `task_failed` — fan-out
 *   parent/child task milestones (Phase 2+).
 */
export const agentActivityKindSchema = z.enum([
  'tool_started',
  'tool_completed',
  'tool_failed',
  'order_created',
  'driver_assigned',
  'client_created',
  'saved_location_created',
  'quote_calculated',
  'subscription_blocked',
  'escalated_to_human',
  'task_started',
  'task_completed',
  'task_failed',
]);
export type AgentActivityKind = z.infer<typeof agentActivityKindSchema>;

/**
 * Cross-references that let us index activities by entity. A single
 * activity may carry multiple refs (e.g. `driver_assigned` carries
 * both `orderId` and `driverId`).
 */
export const agentActivityRefsSchema = z.object({
  orderId: objectIdSchema.optional(),
  clientId: objectIdSchema.optional(),
  driverId: objectIdSchema.optional(),
  vehicleId: objectIdSchema.optional(),
  savedLocationId: objectIdSchema.optional(),
  contactId: objectIdSchema.optional(),
  taskId: objectIdSchema.optional(),
});
export type AgentActivityRefs = z.infer<typeof agentActivityRefsSchema>;

/**
 * `AgentActivity` — bilingual operational audit log.
 *
 * Two surfaces consume it:
 *
 * 1. The chat panel subscribes to activities for the current
 *    conversation and renders them as inline chips between bubbles.
 * 2. The order-page timeline queries by `refs.orderId` and renders
 *    activities alongside the existing system events.
 *
 * `AgentActivity` is **structured** — every row is a single state
 * change with bilingual title templates and entity refs. It is **not**
 * a substitute for chat messages (those live in
 * {@link ConversationMessageSchema}); see `docs/ai/messaging-model.md`.
 */
export const AgentActivitySchema = createModel({
  conversationId: objectIdSchema.optional(),
  /**
   * Owning tenant — real Org only, absent for guest / org-less
   * conversations (same contract as `ConversationSchema.org`).
   */
  org: partySchema.optional(),
  runId: objectIdSchema.optional(),
  taskId: objectIdSchema.optional(),

  kind: agentActivityKindSchema,

  /** English title template, with placeholders interpolated already. */
  titleEn: z.string().min(1),
  /** Arabic title template, with placeholders interpolated already. */
  titleAr: z.string().min(1),

  refs: agentActivityRefsSchema.optional(),

  status: z.enum(['success', 'failed']).optional(),
  error: z.string().optional(),
});

export type AgentActivity = z.infer<typeof AgentActivitySchema.model>;
export type AgentActivityData = z.infer<typeof AgentActivitySchema.data>;
export type AgentActivityPartial = z.infer<typeof AgentActivitySchema.partial>;
export type AgentActivityQueryOne = QueryOne<AgentActivity>;
export type AgentActivityQueryMany = QueryMany<AgentActivity>;
